import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { ClipboardList, Check, History, RefreshCw } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { API } from "../App";
import { PerfectionismResult, PerfectionismTrendChart } from "../components/PerfectionismResult";

const PerfectionismQuestionnairePage = ({ user, logout }) => {
  const { t, locale } = useI18n();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [answers, setAnswers] = useState({});
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState("intro");

  const load = () => {
    axios.get(`${API}/perfectionism/items`, { withCredentials: true, params: { lang: locale } }).then((r) => setData(r.data)).catch(() => toast.error("Questionnaire indisponible"));
    axios.get(`${API}/perfectionism/status`, { withCredentials: true }).then((r) => setStatus(r.data)).catch(() => {});
    axios.get(`${API}/perfectionism/history`, { withCredentials: true }).then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(load, [locale]);

  const items = data?.items || [];
  const answered = Object.keys(answers).length;
  const pending = status?.pending?.[0];
  const canSubmit = items.length > 0 && answered === items.length;

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/perfectionism/submit`, { responses: answers, note, request_id: pending?.request_id }, { withCredentials: true });
      setResult(r.data); setMode("result"); setAnswers({}); setNote("");
      toast.success(t("perfScale.saved", "Questionnaire enregistré"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSubmitting(false); }
  };

  const anchors = data?.anchors || [];
  const rows = useMemo(() => items.map((it) => (
    <div key={it.id} className={`rounded-lg border p-3 sm:p-4 ${answers[it.id] ? "bg-background" : "bg-muted/30"}`} data-testid={`perf-item-${it.id}`}>
      <p className="text-sm font-medium mb-2"><span className="text-muted-foreground mr-1">{it.id}.</span>{it.text}</p>
      <div className="grid grid-cols-7 gap-1">
        {[1, 2, 3, 4, 5, 6, 7].map((v) => (
          <button key={v} type="button" onClick={() => setAnswers((a) => ({ ...a, [it.id]: v }))} title={anchors[v - 1]}
            className={`h-9 rounded-md text-sm border transition-colors ${answers[it.id] === v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`} data-testid={`perf-item-${it.id}-${v}`}>{v}</button>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>{anchors[0]}</span><span>{anchors[6]}</span></div>
    </div>
  )), [items, answers, anchors]);

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6" data-testid="perfectionism-questionnaire-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2"><ClipboardList className="w-7 h-7 text-primary" /> {data?.title || "Questionnaire Perfectionnisme & Excellencisme"}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("perfScale.subtitle", "20 questions · 5 minutes · suivi de votre évolution dans le temps")}</p>
          </div>
          {status?.count > 0 && mode !== "history" && <Button variant="outline" size="sm" onClick={() => setMode("history")} className="gap-1.5" data-testid="perf-history-btn"><History className="w-4 h-4" /> {t("perfScale.history", "Mon évolution")} ({status.count})</Button>}
        </div>

        {pending && mode === "intro" && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm" data-testid="perf-pending-banner">
            {t("perfScale.pending", "Votre thérapeute vous invite à remplir ce questionnaire")}{pending.label ? ` — ${pending.label}` : ""}.
          </div>
        )}

        {mode === "intro" && (
          <Card>
            <CardContent className="p-5 space-y-4">
              <p className="text-sm leading-relaxed">{data?.intro}</p>
              <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1">
                <li>{t("perfScale.dim1", "Excellencisme : viser très haut avec souplesse.")}</li>
                <li>{t("perfScale.dim2", "Standards perfectionnistes : exiger le sans-faute.")}</li>
                <li>{t("perfScale.dim3", "Préoccupations perfectionnistes : peur de l'erreur, doute, auto-critique.")}</li>
              </ul>
              <Button onClick={() => setMode("fill")} data-testid="perf-start-btn" className="gap-2"><Check className="w-4 h-4" /> {status?.count > 0 ? t("perfScale.startAgain", "Refaire le questionnaire") : t("perfScale.start", "Commencer")}</Button>
            </CardContent>
          </Card>
        )}

        {mode === "fill" && (
          <div className="space-y-3">
            <div className="sticky top-14 sm:top-0 z-10 bg-background/95 backdrop-blur py-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1"><span>{answered}/{items.length}</span><span>{data?.subscales?.exc?.hint}</span></div>
              <Progress value={items.length ? (answered / items.length) * 100 : 0} className="h-2" />
            </div>
            {rows}
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={t("perfScale.notePlaceholder", "Note facultative (contexte du moment : examens, surcharge, vacances…)")} className="text-sm" data-testid="perf-note" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMode("intro")}>{t("common.cancel", "Annuler")}</Button>
              <Button onClick={submit} disabled={!canSubmit || submitting} data-testid="perf-submit-btn">{submitting ? "…" : t("perfScale.submit", "Voir mes résultats")}</Button>
            </div>
          </div>
        )}

        {mode === "result" && result && (
          <div className="space-y-4">
            <PerfectionismResult scoring={result.scoring} subscales={data?.subscales} />
            {history.length >= 2 && <PerfectionismTrendChart entries={history} subscales={data?.subscales} />}
            <div className="flex gap-2"><Button variant="outline" onClick={() => setMode("intro")} className="gap-1.5"><RefreshCw className="w-4 h-4" /> {t("common.back", "Retour")}</Button></div>
          </div>
        )}

        {mode === "history" && (
          <div className="space-y-4" data-testid="perf-history">
            {history.length >= 2 ? <PerfectionismTrendChart entries={history} subscales={data?.subscales} /> : <p className="text-sm text-muted-foreground">{t("perfScale.needTwo", "Remplissez le questionnaire au moins deux fois pour voir votre évolution.")}</p>}
            {history.length > 0 && <PerfectionismResult scoring={history[history.length - 1].scoring} subscales={data?.subscales} title={t("perfScale.lastResult", "Dernier résultat")} />}
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">{t("perfScale.allResults", "Toutes les passations")}</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {[...history].reverse().map((e) => (
                  <div key={e.entry_id} className="flex items-center justify-between text-sm p-2 rounded bg-muted/50" data-testid={`perf-entry-${e.entry_id}`}>
                    <span className="text-muted-foreground">{new Date(e.created_at).toLocaleDateString(locale === "en" ? "en-GB" : locale, { day: "numeric", month: "short", year: "numeric" })}{e.label ? ` · ${e.label}` : ""}</span>
                    <span className="flex gap-3 text-xs"><span>EXC {e.scoring.subscales.exc.mean}</span><span>SP {e.scoring.subscales.ps.mean}</span><span>PP {e.scoring.subscales.pc.mean}</span></span>
                  </div>
                ))}
              </CardContent></Card>
            <Button variant="outline" onClick={() => setMode("intro")}>{t("common.back", "Retour")}</Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PerfectionismQuestionnairePage;
